import React, { useEffect, useMemo, useState, useRef } from "react";
import "./SideBar.css";
import CompaniesList from "./CompaniesList/CompaniesList";
import logo from "../../../assets/hk-logo.png";
import searchIcon from "../../../assets/search-icon.png";
import { useChat } from "../Layout/ChatProvider/ChatProvider";
import { fetchNewCompanies, getActiveCompanies, getBusinesses } from "../../../services/api"; // Import the necessary functions
import { jwtDecode } from "jwt-decode";
import { Company } from "../../../types/Company";

interface TokenPayload {
    exp: number;
    business_id: string;
}

type Business = {
    id: string;
    business_name: string;
    [key: string]: any;
};

const Sidebar: React.FC = () => {
    const {
        searchQuery,
        setSearchQuery,
        companies,
        setCompanies, // Make sure to have a function to update the companies state
        companyStatuses,
        selectedStatuses,
        setSelectedStatuses,
        selectedBusinessIds,
        setSelectedBusinessIds,
    } = useChat();

    const [loading, setLoading] = useState(false); // Loading state for fetching companie
    const [business, setBusiness] = useState("");
    const [businesses, setBusinesses] = useState<Business[]>([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [selectedBusinessNames, setSelectedBusinessNames] = useState<string[]>([]); // Keep for display only
    const [allCompanies, setAllCompanies] = useState<Company[]>([]); // Store the full list of companies

    // Ref for the dropdown container
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown on outside click
    useEffect(() => {
        if (!dropdownOpen) return;
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownOpen]);

    // Load searchQuery from localStorage if available
    useEffect(() => {
        const storedSearchQuery = localStorage.getItem("searchQuery");
        if (storedSearchQuery) {
            setSearchQuery(storedSearchQuery);
        }
    }, [setSearchQuery]);

    // Save searchQuery to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem("searchQuery", searchQuery);
    }, [searchQuery, setSearchQuery]);

    useEffect(() => {
        // Only run this effect on the first render (component mount)
        const token = localStorage.getItem("authToken");

        if (token) {
            const decoded = jwtDecode<TokenPayload>(token);
            setBusiness(decoded.business_id);
            console.log("business", decoded.business_id);
        }
    }, []); // Empty dependency array ensures this runs only once on mount

    useEffect(() => {
        if (business === "1") {
            // Only fetch if business_id is 1
            getBusinesses(business).then((data) => {
                setBusinesses(data);
            }).catch((error) => {
                console.error("Error fetching businesses:", error);
            });
        }
    }, [business, setCompanies]);

    // When companies are fetched/set, update allCompanies
    useEffect(() => {
        if (companies.length > 0 && allCompanies.length === 0) {
            setAllCompanies(companies);
        }
    }, [companies, allCompanies.length]);

    // Filter companies by selected businesses
    useEffect(() => {
        if (business === "1") {
            if (selectedBusinessIds.length > 0) {
                const filtered = allCompanies.filter(company =>
                    selectedBusinessIds.map(Number).includes(Number(company.business_id))
                );
                console.log("allCompanies", allCompanies);
                console.log("selectedBusinessIds", selectedBusinessIds);
                console.log("filtered", filtered);
                setCompanies(filtered);
            } else {
                // If no businesses selected, show all companies
                setCompanies(allCompanies);
            }
        }
    }, [selectedBusinessIds.join(','), allCompanies.length, business, setCompanies]);

    const statuses = useMemo(() => {
        const uniqueStatuses = Array.from(
            new Set(companies.map((company) => company.status.status))
        ).filter(Boolean);

        const mapped = uniqueStatuses
            .map((status) => {
                const statusObj = companyStatuses.find((s) => s.status === status);
                return statusObj
                    ? { status: statusObj.status, statusName: statusObj.status_name }
                    : { status, statusName: status };
            })
            .filter((item) => item.statusName);

        return [{ status: "הכל", statusName: "הכל" }, ...mapped];
    }, [companies, companyStatuses]);

    const handleStatusToggle = (status: string) => {
        if (status === "הכל") {
            setSelectedStatuses(["הכל"]);
            return;
        }
        let updated = [...selectedStatuses];
        if (updated.includes(status)) {
            updated = updated.filter((s) => s !== status);
        } else {
            updated = updated.filter((s) => s !== "הכל"); // remove "הכל"
            updated.push(status);
        }
        if (updated.length === 0) {
            updated = ["הכל"];
        }
        setSelectedStatuses(updated);
    };

    const handleRefreshClick = async () => {
        setLoading(true); // Set loading state to true when the refresh button is clicked
        try {
            // Fetch new companies first
            const newCompanies = await fetchNewCompanies();
            console.log("✅ New companies fetched:", newCompanies);

            // After fetching new companies, fetch active companies
            const activeCompanies = await getActiveCompanies();
            setCompanies(activeCompanies); // Set the companies state with the active companies

        } catch (error) {
            console.error("❌ Error fetching new companies:", error);
        } finally {
            setLoading(false); // Set loading state to false after fetching is completed
        }
    };

    const handleBusinessToggle = (biz: Business) => {
        const checked = selectedBusinessIds.includes(biz.id);
        let newIds = [...selectedBusinessIds];
        let newNames = [...selectedBusinessNames];
        if (checked) {
            newIds = newIds.filter(id => id !== biz.id);
            newNames = newNames.filter(name => name !== biz.business_name);
        } else {
            newIds.push(biz.id);
            newNames.push(biz.business_name);
        }
        setSelectedBusinessIds(newIds);
        setSelectedBusinessNames(newNames);
    };

    return (
        <div className="sidebar">
            <div className="sidebar-header-container">
                <button className="button-refresh-companies" onClick={handleRefreshClick}>
                    {loading ? "...טוען רשימת חברות" : "רענן רשימת חברות"}
                </button>
                {business === "1" && (
                    <div className="custom-dropdown-container" ref={dropdownRef}>
                        <div
                            className="custom-dropdown-selected"
                            onClick={() => setDropdownOpen((open) => !open)}
                            dir="rtl"
                        >
                            {selectedBusinessNames.length > 0 ? selectedBusinessNames.join(", ") : "חברת ייעוץ"}
                            <span className="dropdown-arrow" />
                        </div>
                        {dropdownOpen && (
                            <div className="custom-dropdown-list" dir="rtl">
                                {businesses.map((biz) => {
                                    const checked = selectedBusinessIds.includes(biz.id);
                                    return (
                                        <div
                                            key={biz.id}
                                            className={`custom-dropdown-option${checked ? " selected" : ""}`}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => handleBusinessToggle(biz)}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => handleBusinessToggle(biz)}
                                                onClick={e => e.stopPropagation()}
                                                id={`custom-checkbox-${biz.id}`}
                                                style={{ display: "none" }}
                                            />
                                            <label
                                                htmlFor={`custom-checkbox-${biz.id}`}
                                                className="custom-checkbox-label"
                                                tabIndex={0}
                                            >
                                                <span className={`custom-checkbox-svg${checked ? " checked" : ""}`}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <rect x="0.5" y="0.5" width="15" height="15" rx="3.5" stroke="#39ABE2"/>
                                                        {checked && (
                                                            <path d="M4.5 8.5L7 11L11.5 5" stroke="#39ABE2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                        )}
                                                    </svg>
                                                </span>
                                            </label>
                                            {biz.business_name}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
                {business !== "1" && (
                    <img src={logo} alt="AI Assistant Logo" className="chat-avatar-logo" />
                )}
            </div>
            <div className="search-bar-container">
                <input
                    className="search-bar"
                    placeholder="חיפוש לקוח / מנהל תזרים"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <img src={searchIcon} alt="search" className="search-bar-icon" />
            </div>
            <div className="companies-statuses-filter-container">
                {statuses.map((item) => {
                    const count = item.status === "הכל"
                        ? companies.length
                        : companies.filter(c => c.status.status === item.status).length;

                    return (
                        <button
                            key={item.status}
                            className={`status-filter-pill ${
                                selectedStatuses.includes(item.status) ? "selected" : ""
                            }`}
                            onClick={() => handleStatusToggle(item.status)}
                        >
                            {item.statusName}
                            {count > 0 && (
                                <span className="notification-badge">{count}</span>
                            )}
                        </button>
                    );
                })}
            </div>
            <CompaniesList />
        </div>
    );
};

export default Sidebar;
